'use client';

import Image from 'next/image';
import { ImageIcon, ZoomIn } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { Gallery } from '@/types/gallery';
import { useTranslations } from 'next-intl';

interface GalleryCardProps {
  gallery: Gallery;
  onOpen: () => void;
  className?: string;
}

export function GalleryCard({ gallery, onOpen, className = '' }: GalleryCardProps) {
  const t = useTranslations();
  const tGallery = useTranslations('common.gallery');
  
  const firstImage = gallery.images[0];
  const imageCount = gallery.images.length;
  const hasMultipleImages = imageCount > 1;
  
  // Resolve translation keys
  const title = gallery.name ? t(gallery.name) : '';
  const description = gallery.description ? t(gallery.description) : '';

  return (
    <Card
      className={`group cursor-pointer overflow-hidden transition-all hover:shadow-lg hover:scale-105 ${className}`}
      onClick={onOpen}
    >
      <CardContent className="p-0">
        {/* Image Container */}
        <div className="relative overflow-hidden rounded-t-lg h-64">
          {firstImage && (
            <Image
              src={firstImage.src}
              alt={title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover transition-transform group-hover:scale-110"
              loading="lazy"
            />
          )}
          
          {/* Overlay on hover with magnifier icon */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                <ZoomIn className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </div>

          {/* Photo count badge - only show if multiple images */}
          {hasMultipleImages && (
            <div className="absolute end-3 bottom-3 flex items-center gap-2 rounded-lg bg-black/80 px-3 py-2 text-sm font-semibold text-white shadow-xl backdrop-blur-sm">
              <ImageIcon className="h-5 w-5" />
              <span>{tGallery('photo_count', { count: imageCount })}</span>
            </div>
          )}
        </div>

        {/* Title and Description */}
        <div className="p-6">
          {title && (
            <h3 className="text-lg font-semibold text-teal-900 mb-2">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-gray-600 text-sm">
              {description}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
